<script setup lang="ts">
import { RouterLink } from 'vue-router'
import { useCorporativeAccount } from '@/store/corporative'
import bgCreateCorporativeAccount from '@/assets/images/bg-criar-conta-corporativa.png'
import AccountItem from '@/components/Header/AccountItem.vue'
import plusIcon from '@/assets/icons/black/plus.svg'

import { storeToRefs } from 'pinia'
import { useI18n } from 'vue-i18n'
import useStore from '@/store/auth'
import { useDisplay } from 'vuetify'
import { ref, computed } from 'vue'

const accountCorporativeStore = useCorporativeAccount()
const { corporates, selectedAccount } = storeToRefs(accountCorporativeStore)
const authStore = useStore()
const { user } = storeToRefs(authStore)
const { mobile } = useDisplay()

const sortedCorporates = computed(() => {
  return [...(corporates.value || [])].sort((a, b) =>
    a.tradingName.localeCompare(b.tradingName, 'pt-BR', { sensitivity: 'base' })
  )
})

const { t } = useI18n()
const isMobile = computed(() => mobile.value)

const menuOpen = ref(false)

const emit = defineEmits(['update:modelValue'])
</script>

<template>
  <div>
    <v-menu v-if="!isMobile" v-model="menuOpen" location="end">
      <template #activator="{ props }">
        <v-btn
          id="user-menu"
          class="elevation-0"
          icon="mdi-chevron-down"
          :class="{ rotated: menuOpen }"
          v-bind="props"
          :ripple="false"
          :height="isMobile ? '48px' : '40px'"
          :width="isMobile ? '48px' : '40px'"
        ></v-btn>
      </template>
      <v-list class="ml-4" max-width="300px" style="border-radius: 16px">
        <v-list-item
          class="ma-2 pa-2"
          style="
            background-color: rgba(141, 198, 63, 0.2);
            min-height: 130px;
            border-radius: 10px !important;
          "
        >
          <RouterLink
            class="d-flex flex-column align-center w-100 text-center"
            :to="
              selectedAccount == null
                ? { name: 'home' }
                : {
                    name: 'events-list',
                    params: { id: selectedAccount.id },
                  }
            "
          >
            <img
              :src="selectedAccount?.logo || user?.profilePicture"
              style="width: 56px; margin-bottom: 8px; border-radius: 4px"
            />
            <span style="font-weight: 600; font-size: 16px">
              {{ selectedAccount?.tradingName || $t('header.personalAccount') }}
            </span>
            <span
              style="
                font-weight: 400;
                font-size: var(--fontSize-77, 12px);
                color: var(
                  --standart-theme---EVE-opacity-text--secondary,
                  rgba(0, 0, 0, 0.6)
                );
              "
            >
              {{
                selectedAccount
                  ? t('header.corporateAccount')
                  : t('header.personalAccount')
              }}
            </span>
          </RouterLink>
        </v-list-item>

        <v-list-item v-if="corporates?.length" :as="AccountItem" height="56px">
          <RouterLink
            id="create-corporate-account"
            :to="{ name: 'create-corporative-account' }"
            class="d-flex align-center"
          >
            <img :src="plusIcon" alt="" width="24px" />
            <span class="text-body-1 text-grey-darken-2 ml-2">
              {{ t('header.createCorporativeAccount') }}
            </span>
          </RouterLink>
        </v-list-item>
        <v-list-item v-else id="create-first-corporative-account">
          <RouterLink :to="{ name: 'create-corporative-account' }">
            <div
              style="
                display: flex;
                align-items: center;
                position: relative;
                width: 100%;
                height: 80px;
              "
            >
              <img
                :src="bgCreateCorporativeAccount"
                style="
                  position: absolute;
                  top: 0;
                  left: 0;
                  width: 100%;
                  height: 100%;
                  z-index: 20;
                "
              />
              <div
                style="
                  position: absolute;
                  z-index: 30;
                  width: 100%;
                  align-self: center;
                  font-weight: bold;
                  padding: 1rem 0;
                  text-align: center;
                  color: white;
                "
              >
                {{ t('header.createCorporativeAccount') }}
              </div>
            </div>
          </RouterLink>
        </v-list-item>
        <v-divider style="opacity: 0.9" />
        <div style="max-height: 300px; overflow-y: auto">
          <v-list-item
            id="user-menu-item-personal-account"
            style="width: 100%; height: 60px"
            :style="
              selectedAccount == null
                ? 'border-width: 1px; border-color: #8DC63F; background-color: rgba(141, 198, 63, 0.2);'
                : ''
            "
          >
            <RouterLink :to="{ name: 'home' }">
              <AccountItem
                :img="user?.profilePicture"
                :text="$t('header.personalAccount')"
                :subtitle="t('header.personalAccount')"
                :personal="true"
              />
            </RouterLink>
          </v-list-item>

          <v-list-item
            v-for="(account, index) in sortedCorporates"
            :key="index"
            style="width: 100%; height: 60px"
            :style="
              selectedAccount?.id === account.id
                ? 'border-width: 1px; border-color: #8DC63F; background-color: rgba(141, 198, 63, 0.2);'
                : ''
            "
          >
            <RouterLink
              :id="`user-menu-item-corporative-account-${index}`"
              :to="{
                name: 'events-list',
                params: { id: account.id },
              }"
            >
              <AccountItem
                :id="account.id"
                :img="account.logo as string"
                :text="account.tradingName"
                :subtitle="t('header.corporateAccount')"
              />
            </RouterLink>
          </v-list-item>
        </div>
      </v-list>
    </v-menu>

    <v-list v-else>
      <v-divider style="opacity: 0.9; margin-top: 8px" />
      <v-list-item v-if="corporates?.length" :as="AccountItem" height="60px">
        <RouterLink
          id="create-corporate-account"
          :to="{ name: 'create-corporative-account' }"
          class="d-flex align-center"
        >
          <img
            :src="plusIcon"
            alt=""
            width="32px"
            style="margin-right: 1rem; border-radius: 12px"
          />
          <span class="text-body-1" style="color: var(--v-theme-grey-5)">
            {{ t('header.createCorporativeAccount') }}
          </span>
        </RouterLink>
      </v-list-item>
      <v-list-item v-else id="create-first-corporative-account">
        <RouterLink :to="{ name: 'create-corporative-account' }">
          <div
            style="
              display: flex;
              align-items: center;
              position: relative;
              width: 100%;
              height: 80px;
            "
          >
            <img
              :src="bgCreateCorporativeAccount"
              style="
                position: absolute;
                top: 0;
                left: 0;
                width: 100%;
                height: 100%;
                z-index: 20;
              "
            />
            <div
              style="
                position: absolute;
                z-index: 30;
                width: 100%;
                align-self: center;
                font-weight: bold;
                padding: 1rem 0;
                text-align: center;
                color: white;
              "
            >
              {{ t('header.createCorporativeAccount') }}
            </div>
          </div>
        </RouterLink>
      </v-list-item>
      <div style="max-height: 300px; overflow-y: auto">
        <v-list-item
          id="user-menu-item-personal-account"
          style="width: 100%; height: 60px"
          :style="
            selectedAccount == null
              ? 'border-width: 1px; border-color: #8DC63F; background-color: rgba(141, 198, 63, 0.2);'
              : ''
          "
        >
          <RouterLink :to="{ name: 'home' }">
            <AccountItem
              :img="user?.profilePicture"
              :text="$t('header.personalAccount')"
              :subtitle="t('header.personalAccount')"
              :personal="true"
            />
          </RouterLink>
        </v-list-item>
        <v-list-item
          v-for="(account, index) in sortedCorporates"
          :key="index"
          :style="{
            height: '60px !important',
            width: '100%',
            borderWidth: selectedAccount?.id === account.id ? '1px' : '',
            borderColor: selectedAccount?.id === account.id ? '#8DC63F' : '',
            backgroundColor:
              selectedAccount?.id === account.id
                ? 'rgba(141, 198, 63, 0.2)'
                : '',
          }"
        >
          <RouterLink
            :id="`user-menu-item-corporative-account-${index}`"
            :to="{
              name: 'events-list',
              params: { id: account.id },
            }"
          >
            <AccountItem
              :id="account.id"
              :img="account.logo as string"
              :text="account.tradingName"
              :subtitle="t('header.corporateAccount')"
            />
          </RouterLink>
        </v-list-item>
      </div>
    </v-list>
  </div>
</template>

<style scoped>
.v-btn__overlay,
.v-btn__underlay {
  opacity: 0 !important;
}

.rotated {
  transform: rotate(270deg);
  transition: transform 0.3s ease;
}

img {
  transition: transform 0.3s ease;
}

.v-list-item__content {
  width: 100% !important;
}

::-webkit-scrollbar-track {
  margin-top: 10px !important;
  margin-bottom: 10px !important;
}
</style>
